// client/src/routes/portal/route.tsx — the /portal layout · PORTAL-002 phase 1 (04 §6.6,
// §8.16, §9.2.2).
//
// The sibling of `app/route.tsx`: the shell is rendered ONCE around every `/portal/*` screen,
// and the guard decides who gets to see it.
//
// ── The guard admits on a LEASE, and never on a permission ─────────────────────────────────────
// `/app` gates on permissions and sends the `tenant` role away; this one gates on the
// caller being a party to at least one contract (D-1727) and sends everybody else back
// to the app. Two consequences worth stating, because both are the point of the item:
//
//   • The owner navigation is unreachable from here even by URL — not because this shell
//     declines to link to it, but because `/app`'s own guard bounces a tenant back (04 §9.2.2)
//     and this file's `PortalShell` imports no owner nav code at all (`portal-nav.ts`).
//   • An `owner`, `manager`, `accountant` or `admin` who types `/portal` lands back in the app
//     rather than on SCR-SYS-403. A landlord is not FORBIDDEN from the portal — the portal is
//     simply not their product surface, and „Acces interzis" would read as a bug in their
//     account. A tenant reaching `/app` is treated the same way, in the same direction.
//
// With ONE account excepted, and it is the account those two bullets cannot both hold
// for: a `tenant`-role login that is party to no contract. `/app` bounces it here on the
// role and this guard used to bounce it back on the count — a ring the router ends by
// throwing „Too many redirects" (D-2010). It is admitted instead, and every screen under
// the shell already says the true thing through `ContractScope`: „Nu ai niciun contract
// în portal".
//
// D-184 is closed here: UI-003 had to write the tenant redirect as an untyped `href` because
// `/portal` did not exist. It exists, so both directions are typed and the route tree checks
// them.
//
// ── the search schema belongs HERE, not on the five screens ────────────────────────────────
// Phase 2 adds `validateSearch`. `?contract=` is the portal's SCOPE — which of the caller's
// leases the header selector, „Acasă", „Chiria mea", „Contoare" and the incident report form are
// all showing — and a scope declared on the layout route is part of the full search of every
// screen under it. That is what makes it one fact instead of five components' state, and it is
// what lets `PortalShell` read it: the shell is mounted by this route, so a param declared on
// any one child would be invisible to it. See D-1180.
//
// ── the second guard: a multi-contract tenant is never here without a named contract ──────
// Every screen under this shell is a view of ONE contract, and `scope.ts` used to fill an
// unnamed scope with a silent default — indistinguishable, on screen, from „this is everything
// you rent". So the guard asks instead: a tenant with several contracts whose URL names none of
// them is sent to `/select-contract` with the deep link in `redirect`, the portal's sibling of
// SCR-AUTH-007, and comes back with the contract written into the URL. From then on every tab and
// link carries it (D-1180) and the question is never asked again for that visit. A tenant with
// one contract never sees the page; a foreign or stale id is treated as no id (D-1502).
//
// The count comes through the same query key every screen uses, so it is cached for the screen
// the tenant lands on rather than fetched twice. A failed read lets the visitor through: the
// screen renders that failure itself, with a retry, and a guard that turned it into a redirect
// would hide the button.

import { listPortalContractsOptions } from '@/client/@tanstack/react-query.gen';

import { Link, Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { toProblemDetails } from '@/api/problem-details';
import { PortalShell } from '@/components/layout/PortalShell';
import { SystemState } from '@/components/ui/states';
import { DEFAULT_LANDING } from '@/features/auth/redirect';
import { hasSeveralContracts } from '@/features/portal/scope';
import { portalSearchSchema } from '@/features/portal/search';
import { ensureSession, isPortalTenant, useHasSession } from '@/session';

export const Route = createFileRoute('/portal')({
  validateSearch: portalSearchSchema,
  beforeLoad: async ({ context, location, search }) => {
    const session = await ensureSession();
    if (!session) throw redirect({ to: '/login', search: { redirect: location.href } });
    // NOT an equality on the role (D-1727). The role describes the active organization, and
    // being somebody's tenant is not a role: a landlord who has accepted a portal invitation
    // from another landlord holds `owner` here and is a party to a lease all the same. What
    // admits is the lease itself, and an account that is party to none still lands in /app.
    //
    // Unless `tenant` IS the role its token names, and then `/app` is not a landing
    // either: that role holds `Portal.Self` and nothing else (03 §17.1), so every screen
    // over there answers „Acces interzis" and `/app`'s own guard sends the account straight
    // back here. Sending it away was a redirect loop rather than a destination (D-2010). It
    // comes in and reads the one sentence that fits it — `ContractScope`'s empty state, on
    // all five tabs.
    //
    // And unless the role is `building_resident`: an account whose only memberships are clădiri it
    // lives in (D-3343). `/app` sends it here as it sends a `tenant` (D-3372), so sending it back
    // would be D-2010's ring again. The portal shows it the „Clădire" tab alone, and its bare
    // `/portal` — whose „Acasă" can only say it rents nothing — leads to that tab.
    const buildingOnly = session.role === 'building_resident';
    if (!isPortalTenant(session) && session.role !== 'tenant' && !buildingOnly) {
      throw redirect({ href: DEFAULT_LANDING });
    }
    if (buildingOnly && !isPortalTenant(session) && location.pathname.replace(/\/$/, '') === '/portal') {
      throw redirect({ to: '/portal/building' });
    }

    const contracts = await context.queryClient
      .ensureQueryData(listPortalContractsOptions())
      .catch(() => undefined);
    if (
      hasSeveralContracts(contracts)
      && !contracts?.some((contract) => contract.id === search.contract)
    ) {
      throw redirect({ to: '/select-contract', search: { redirect: location.href } });
    }
  },
  component: PortalLayout,
  // §8.20: both system states render INSIDE the active shell, and for anything under
  // `/portal` the active shell is this one.
  errorComponent: PortalErrorComponent,
  notFoundComponent: PortalNotFoundComponent,
});

function PortalLayout() {
  // Nothing while a sign-out is between clearing the session and reaching `/login` (D-4041).
  const signedIn = useHasSession();
  if (!signedIn) return null;
  return (
    <PortalShell>
      <Outlet />
    </PortalShell>
  );
}

/**
 * SCR-SYS-500, inside the portal shell. Its one button carries `min-h-11`, as every portal
 * button does: a default `btn` under this theme is 40 px, under §11.4's floor (D-2284).
 */
function PortalErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const { t } = useTranslation();
  const problem = toProblemDetails(error);
  return (
    <SystemState
      icon="exclamation-triangle"
      title={t('errors.title')}
      body={problem.detail ?? t('errors.generic')}
      correlationId={problem.correlationId}
      actions={
        <button type="button" className="btn btn-primary min-h-11" onClick={reset}>
          {t('common.retry')}
        </button>
      }
    />
  );
}

/** SCR-SYS-404, inside the portal shell — the way back is „Acasă", not the owner dashboard. */
function PortalNotFoundComponent() {
  const { t } = useTranslation();
  return (
    <SystemState
      icon="question-mark-circle"
      title={t('errors.notFoundTitle')}
      body={t('errors.notFoundBody')}
      actions={
        <Link to="/portal" className="btn btn-primary min-h-11">
          {t('portal.nav.home')}
        </Link>
      }
    />
  );
}
